import { useState, useEffect, useMemo } from "react";
import { format, parseISO, isAfter, isBefore, differenceInHours } from "date-fns";
import { CalendarDays, Plane, Building2, Car, MapPin, Info, Star, Cloud, Compass, Utensils, Tickets } from "lucide-react";
import { Trip, TripElement, FlightDetails } from "@/types/trip";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackTimelineAction, trackTripCardAction } from "@/utils/analytics";
import DeleteTrip from "./DeleteTrip";
import { getFlightStatus, FlightStatus, getFlightStatusLabel } from "@/utils/flightStatus";
import FlightSegment from "./segments/FlightSegment";
import { TripCardElement } from "./TripCardElement";
import { toast } from "@/hooks/use-toast";

interface FullScreenTripTimelineProps {
  trip: Trip;
  getAirportInfo: (flightElement: any, type: 'departure' | 'arrival') => any;
  handleCheckFlightStatus?: (flightNumber: string) => void;
  onWeatherClick?: () => void;
  onActivitiesClick?: () => void;
  onFoodClick?: () => void;
}

const FullScreenTripTimeline = ({ 
  trip, 
  getAirportInfo,
  handleCheckFlightStatus,
  onWeatherClick,
  onActivitiesClick,
  onFoodClick
}: FullScreenTripTimelineProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Initialize with all elements expanded
  const [expandedElementIds, setExpandedElementIds] = useState<string[]>([]);
  const [flightStatusData, setFlightStatusData] = useState<Record<string, FlightStatus>>({});

  // Use trip.identifiedDestination consistently for the destination name
  const destinationName = trip.identifiedDestination || "";

  // Enhanced debug logs to track destination names and device mode
  console.debug("🏙️ FullScreenTripTimeline DEBUG:", {
    tripId: trip.id,
    isMobile,
    raw_destinationName: destinationName,
    tripIdentifiedDestination: trip.identifiedDestination,
    finalDisplayedDestination: destinationName || t("tripCard.destination", "Destination")
  });

  // Memoize the sorted elements to prevent recreating on every render
  const sortedElements = useMemo(() => 
    [...trip.elements].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    ), 
    [trip.elements]
  );

  const now = new Date();

  const ongoingElement = sortedElements.find(element => {
    const startDate = parseISO(element.startDate || "1970");
    const endDate = parseISO(element.endDate || "1970");
    return isAfter(now, startDate) && isBefore(now, endDate);
  });

  const nextElement = !ongoingElement ? sortedElements.find(element => {
    const startDate = parseISO(element.startDate || "1970");
    return isAfter(startDate, now);
  }) : null;

  const highlightedElementId = ongoingElement 
    ? `${ongoingElement.type}-${sortedElements.indexOf(ongoingElement)}`
    : nextElement 
      ? `${nextElement.type}-${sortedElements.indexOf(nextElement)}`
      : null;

  // Expand all elements by default when component mounts
  useEffect(() => {
    const allElementIds = sortedElements.map((element, index) => 
      `${element.type}-${index}`
    );
    setExpandedElementIds(allElementIds);
  }, [sortedElements]);

  const toggleExpand = (id: string, elementType: string) => {
    if (expandedElementIds.includes(id)) {
      setExpandedElementIds(expandedElementIds.filter(elementId => elementId !== id));
    } else {
      setExpandedElementIds([...expandedElementIds, id]);
      trackTimelineAction(
        "expand_element",
        elementType as "flight" | "hotel" | "car" | "activity",
        trip.id,
        destinationName
      );
    }
  };

  // Fetch flight status data when the component mounts
  useEffect(() => {
    const fetchFlightStatus = async () => {
      try {
        // Get all flight elements
        const flightElements = trip.elements.filter(element => element.type === 'flight');

        if (flightElements.length === 0) return;

        // Create flight IDs for API call
        const flightIds = flightElements.map(element => {
          const flightDetails = element.details as FlightDetails;
          
          // Extract carrier code from the flight number (first 2 letters)
          const carrierCode = flightDetails.flightNumber.match(/^[A-Z]+/)?.[0] || flightDetails.airline || 'XX';
          
          // Extract numeric flight number without airline code
          const numericFlightNumber = flightDetails.flightNumber.replace(/^[A-Z]+/, '');
          
          // Create flight ID with proper airline code
          return `${carrierCode}${numericFlightNumber}_${flightDetails.departure.code}_${format(parseISO(flightDetails.departure.time || "1970"), 'yyyy-MM-dd')}`;
        });

        console.debug("Fetching flight status for IDs:", flightIds);

        // Fetch flight status data
        const statusData = await getFlightStatus(flightIds);

        // Convert array to record for easier lookup
        const statusRecord: Record<string, FlightStatus> = {};
        statusData.forEach(status => {
          statusRecord[status.id] = status;
        });

        setFlightStatusData(statusRecord);
      } catch (error) {
        console.error('Error fetching flight status:', error);
      }
    };

    fetchFlightStatus();
  }, [trip.elements]);

  // Function to get flight status display for an element
  const getFlightStatusDisplay = (element: TripElement) => {
    if (element.type !== 'flight') return null;

    const flightDetails = element.details as FlightDetails;

    // Extract carrier code from the flight number (first 2 letters)
    const carrierCode = flightDetails.flightNumber.match(/^[A-Z]+/)?.[0] || flightDetails.airline || 'XX';
    
    // Extract numeric flight number without airline code
    const numericFlightNumber = flightDetails.flightNumber.replace(/^[A-Z]+/, '');
    
    // Create flight ID with proper airline code
    const flightId = `${carrierCode}${numericFlightNumber}_${flightDetails.departure.code}_${format(parseISO(flightDetails.departure.time || "1970"), 'yyyy-MM-dd')}`;

    // Get flight status from state
    const flightStatus = flightStatusData[flightId];

    // Only show status if we have a valid response from the API
    if (flightStatus && flightStatus.latestStatus) {
      // Check if flight is canceled
      const isCanceled = flightStatus.latestStatus.flightStatus?.toLowerCase().includes('cancel') || false;
      
      // Get departure status
      const departureStatus = flightStatus.latestStatus.estimatedDepartureLocalDate
        ? getFlightStatusLabel(
            flightStatus.scheduledDepartureLocalDate,
            flightStatus.latestStatus.estimatedDepartureLocalDate
          )
        : null;
      
      // Return null instead of the status element to hide it
      return null;
    }
    
    // If no flight status data, don't show any status
    return null;
  };

  const handleFlightStatusCheck = (flightNumber: string) => {
    if (handleCheckFlightStatus) {
      trackTimelineAction(
        "check_flight_status",
        "flight",
        trip.id,
        destinationName
      );
      handleCheckFlightStatus(flightNumber);
    }
  };

  const isWithinHours = (departureTime: string, hours: number): boolean => {
    try {
      const departure = new Date(departureTime);
      const hoursInMs = hours * 60 * 60 * 1000;
      return (departure.getTime() - now.getTime()) <= hoursInMs && departure.getTime() > now.getTime();
    } catch (e) {
      console.error("Error calculating flight time:", e);
      return false;
    }
  };

  const handleWeatherButtonClick = () => {
    if (onWeatherClick) {
      trackTripCardAction("weather", trip.id, destinationName);
      onWeatherClick();
    }
  };

  const handleActivitiesButtonClick = () => {
    if (onActivitiesClick) {
      trackTripCardAction("activities", trip.id, destinationName);
      onActivitiesClick();
    }
  };

  const handleFoodButtonClick = () => {
    if (onFoodClick) {
      trackTripCardAction("food", trip.id, destinationName);
      onFoodClick();
    }
  };

  // Function to open hotel location in Google Maps
  const openHotelOnMap = (hotelName: string, hotelCity: string) => {
    // Track the map view action
    trackTimelineAction(
      "view_on_map",
      "hotel",
      trip.id,
      trip.identifiedDestination || ""
    );
    
    // Create the Google Maps URL with the hotel name and city
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${hotelName} ${hotelCity}`
    )}`;
    
    // Open in a new tab
    window.open(mapsUrl, '_blank');
  };

  // Get the display destination name - use identifiedDestination or fallback
  const displayDestinationName = destinationName || t("tripCard.destination", "Destination");

  return (
    <div className="p-4">
      {!isMobile && trip.destinationImage && (
        <div className="mb-6 overflow-hidden rounded-lg shadow-md">
          <div className="relative">
            <img 
              src={trip.destinationImage} 
              alt={displayDestinationName} 
              className="w-full h-48 object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
              }}
            />
            {trip.bookingReference && (
              <div className="absolute z-10 bottom-3 left-3 bg-black/30 backdrop-blur-md rounded-full px-3 py-1 z-10">
                <span className="text-white text-xs font-medium flex items-center cursor-pointer"
                onClick={() => {
                  navigator.clipboard.writeText(trip.bookingReference);
                  toast({
                    description: t("tripCard.bookingReferenceCopied", "Booking reference copied to clipboard"),
                    duration: 1000,
                  });
                }}>
                    <Tickets className="w-4 h-4 mr-1.5 " />
                    {trip.bookingReference}
                </span>
              </div>
            )}
          </div>
          
          <div className="p-3 bg-white">
            <h2 className="text-xl font-bold text-travel-700">
              {displayDestinationName}
            </h2>
          </div>
        </div>
        
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-travel-500" />
          <span className="text-travel-500 font-medium">{trip.dates}</span>
        </div>
        <DeleteTrip 
          tripId={trip.id} 
          tripName={displayDestinationName} 
        />
      </div>

      <div className={`${isMobile ? '' : 'md:flex md:gap-6'}`}>
        <div className={`${isMobile ? 'w-full' : 'md:w-2/3'}`}>
          <div className="space-y-4 pb-16">
            {sortedElements.map((element, index) => {
              const elementId = `${element.type}-${index}`;
              const isExpanded = expandedElementIds.includes(elementId);
              const isHighlighted = elementId === highlightedElementId;
              
              return (
                <motion.div
                  key={elementId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="relative"
                >
                  {index > 0 && (
                    <div className="absolute top-0 left-6 -translate-x-1/2 h-4 w-0.5 bg-travel-300 -mt-4" />
                  )}
                  
                  <div 
                    className={`relative border rounded-lg overflow-hidden shadow-sm 
                      ${isExpanded ? 'bg-travel-50 border-travel-200' : 'bg-white border-gray-200'}
                      ${isHighlighted ? 'ring-2 ring-travel-500 shadow-md' : ''}
                      transition-all duration-200 ease-in-out hover:shadow-md`}
                    onClick={() => toggleExpand(elementId, element.type)}
                  >
                    <div className={`absolute left-6 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 
                      ${isHighlighted ? 'border-travel-500 bg-travel-100' : 'border-travel-500 bg-white'} 
                      z-10 w-3 h-3`} />
                    
                    <div className={`flex items-start p-4 pl-10 ${isHighlighted ? 'bg-travel-50' : ''}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {element.type === 'flight' && <Plane className={`w-4 h-4 ${isHighlighted ? 'text-travel-600' : 'text-travel-500'}`} />}
                          {element.type === 'hotel' && <Building2 className={`w-4 h-4 ${isHighlighted ? 'text-travel-600' : 'text-travel-500'}`} />}
                          {element.type === 'car' && <Car className={`w-4 h-4 ${isHighlighted ? 'text-travel-600' : 'text-travel-500'}`} />}
                          {element.type === 'activity' && <MapPin className={`w-4 h-4 ${isHighlighted ? 'text-travel-600' : 'text-travel-500'}`} />}
                          
                          <span className={`font-semibold ${isHighlighted ? 'text-travel-900' : 'text-gray-800'}`}>
                            {format(parseISO(element.startDate || "1970"), 'EEE, MMM d')}
                          </span>
                        </div>
                        
                        {getElementPreview(element, getAirportInfo)}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-4 pb-4 pl-10"
                      >
                        {element.type === 'flight' && (
                          <div className="mt-2">
                            <FlightSegment
                              flightDetails={element.details as FlightDetails}
                              departureAirport={getAirportInfo(element, 'departure')}
                              arrivalAirport={getAirportInfo(element, 'arrival')}
                              isWithin72Hours={isWithinHours((element.details as FlightDetails).departure.time, 72)}
                              isWithin48Hours={isWithinHours((element.details as FlightDetails).departure.time, 48)}
                              handleCheckFlightStatus={handleFlightStatusCheck}
                              tripId={trip.id} 
                              destinationName={trip.identifiedDestination || ""}
                              showAddToCalendarButton={true} 
                              showETicketButton={false}
                              isDirectFlight={sortedElements.filter(el => el.type === 'flight').length === 1}
                            />
                          </div>
                        )}
                        
                        {element.type === 'hotel' && (
                          <div className="mt-2 bg-travel-50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-2 text-travel-600">
                              <Building2 className="w-4 h-4" />
                              <span className="font-semibold text-sm">
                                {(element.details as any).name}
                              </span>
                            </div>
                            <div className="text-sm text-travel-500">
                              {(element.details as any).city}
                            </div>
                            <div className="flex justify-between text-xs text-travel-500 pt-1">
                              <span>{format(parseISO(element.startDate || "1970"), 'MMM d')}</span>
                              <span>→</span>
                              <span>{format(parseISO(element.endDate || "1970"), 'MMM d')}</span>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full mt-2 bg-white text-travel-600 border-travel-200 hover:bg-travel-50 hover:text-travel-700"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent event from bubbling up to parent
                                openHotelOnMap((element.details as any).name, (element.details as any).city);
                              }}
                            >
                              <MapPin className="mr-1 h-4 w-4" />
                              {t("tripCard.viewOnMap", "View on Map")}
                            </Button>
                          </div>
                        )}
                        
                        {element.type !== 'flight' && element.type !== 'hotel' && (
                          <div className="mt-2">
                            <TripCardElement 
                              element={element}
                              getAirportInfo={getAirportInfo}
                              handleCheckFlightStatus={handleFlightStatusCheck}
                              tripId={trip.id}
                              destinationName={trip.identifiedDestination || ""}
                            />
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
        
        {!isMobile && (
          <div className="md:w-1/3 bg-white rounded-lg border border-gray-200 p-4 shadow-sm h-fit sticky top-4">
            <h3 className="font-semibold text-travel-700 mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              {t("tripCard.tripSummary", "Trip Summary")}
            </h3>
            
            <div className="space-y-3">
              <div>
                <h4 className="text-sm text-travel-600 font-medium">{t("tripCard.destination", "Destination")}</h4>
                <p className="text-gray-700">{displayDestinationName}</p>
              </div>
              
              <div>
                <h4 className="text-sm text-travel-600 font-medium">{t("tripCard.dates", "Dates")}</h4>
                <p className="text-gray-700">{trip.dates}</p>
              </div>
              
              <div>
                <h4 className="text-sm text-travel-600 font-medium">{t("tripCard.tripElements", "Trip Elements")}</h4>
                <ul className="text-gray-700 list-disc list-inside">
                  {trip.elements.filter(e => e.type === 'flight').length > 0 && (
                    <li>{trip.elements.filter(e => e.type === 'flight').length} {t("tripCard.flights", "Flights")}</li>
                  )}
                  {trip.elements.filter(e => e.type === 'hotel').length > 0 && (
                    <li>{trip.elements.filter(e => e.type === 'hotel').length} {t("tripCard.hotels", "Hotels")}</li>
                  )}
                  {trip.elements.filter(e => e.type === 'car').length > 0 && (
                    <li>{trip.elements.filter(e => e.type === 'car').length} {t("tripCard.cars", "Car Rentals")}</li>
                  )}
                  {trip.elements.filter(e => e.type === 'activity').length > 0 && (
                    <li>{trip.elements.filter(e => e.type === 'activity').length} {t("tripCard.activities", "Activities")}</li>
                  )}
                </ul>
              </div>
              
              {!isMobile && (
                <div className="pt-2">
                  <h4 className="text-sm text-travel-600 font-medium mb-2">{t("tripCard.destinationActions", "Destination Actions")}</h4>
                  <div className="flex flex-wrap gap-2">
                    {onWeatherClick && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex items-center gap-1 text-xs py-1 h-8"
                        onClick={handleWeatherButtonClick}
                      >
                        <Cloud className="w-3 h-3" />
                        {t("tripCard.weather", "Weather")}
                      </Button>
                    )}
                    
                    {onActivitiesClick && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex items-center gap-1 text-xs py-1 h-8"
                        onClick={handleActivitiesButtonClick}
                      >
                        <Compass className="w-3 h-3" />
                        {t("tripCard.activities", "Activities")}
                      </Button>
                    )}
                    
                    {onFoodClick && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex items-center gap-1 text-xs py-1 h-8"
                        onClick={handleFoodButtonClick}
                      >
                        <Utensils className="w-3 h-3" />
                        {t("tripCard.food", "Food & Drink")}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getElementPreview = (element: TripElement, getAirportInfo: any) => {
  switch (element.type) {
    case 'flight':
      const flightDetails = element.details as FlightDetails;
      const departureAirport = getAirportInfo(element, 'departure');
      const arrivalAirport = getAirportInfo(element, 'arrival');
      
      return (
        <div className="mt-1 text-sm text-gray-600">
          <div className="flex justify-between">
            <span>{departureAirport.cityName || flightDetails.departure.code}</span>
            <span>→</span>
            <span>{arrivalAirport.cityName || flightDetails.arrival.code}</span>
          </div>
        </div>
      );
      
    case 'hotel':
      return (
        <div className="mt-1 text-sm text-gray-600">
          {(element.details as any).name}
        </div>
      );
      
    case 'car':
      return (
        <div className="mt-1 text-sm text-gray-600">
          {(element.details as any).name}
        </div>
      );
      
    case 'activity':
      return (
        <div className="mt-1 text-sm text-gray-600">
          {(element.details as any).name}
        </div>
      );
      
    default:
      return null;
  }
};

export default FullScreenTripTimeline;
